import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, ChevronRight, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const numInputCls =
  "bg-[#0E1015] border-[#262933] focus-visible:ring-nitro text-neutral-100 h-10";

const EMPTY_ENGINE = { name: "", fuel: "petrol", stock_bhp: "", stock_nm: "", tuned_bhp: "", tuned_nm: "" };

export const VehicleManager = () => {
  const [catalogue, setCatalogue] = useState(null);
  const [openMake, setOpenMake] = useState(null);
  const [openModel, setOpenModel] = useState(null);
  const [newMake, setNewMake] = useState("");
  const [newModel, setNewModel] = useState("");
  const [eng, setEng] = useState(EMPTY_ENGINE);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios
      .get(`${API}/vehicles/catalogue`, { withCredentials: true })
      .then((r) => setCatalogue(r.data.catalogue))
      .catch(() => setCatalogue([]));

  useEffect(() => {
    load();
  }, []);

  const call = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      await load();
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const addMake = () =>
    call(
      () => axios.post(`${API}/vehicles/manufacturers`, { make: newMake }, { withCredentials: true }),
      `Added ${newMake}`
    ).then(() => setNewMake(""));

  const deleteMake = (make) => {
    if (!window.confirm(`Delete ${make} and all its models/engines?`)) return;
    call(
      () => axios.delete(`${API}/vehicles/manufacturers/${encodeURIComponent(make)}`, { withCredentials: true }),
      `${make} removed`
    );
  };

  const addModel = (make) =>
    call(
      () => axios.post(`${API}/vehicles/models`, { make, model: newModel }, { withCredentials: true }),
      `Added ${newModel}`
    ).then(() => setNewModel(""));

  const deleteModel = (make, model) => {
    if (!window.confirm(`Delete ${model}?`)) return;
    call(
      () => axios.delete(`${API}/vehicles/models`, { params: { make, model }, withCredentials: true }),
      `${model} removed`
    );
  };

  const engineReady =
    eng.name.trim() && eng.stock_bhp && eng.stock_nm && eng.tuned_bhp && eng.tuned_nm;

  const addEngine = (make, model) =>
    call(
      () =>
        axios.post(
          `${API}/vehicles/engines`,
          {
            make,
            model,
            name: eng.name,
            fuel: eng.fuel,
            stock_bhp: parseInt(eng.stock_bhp, 10),
            stock_nm: parseInt(eng.stock_nm, 10),
            tuned_bhp: parseInt(eng.tuned_bhp, 10),
            tuned_nm: parseInt(eng.tuned_nm, 10),
          },
          { withCredentials: true }
        ),
      "Engine added"
    ).then(() => setEng(EMPTY_ENGINE));

  const deleteEngine = (make, model, engine) => {
    if (!window.confirm(`Delete engine "${engine}"?`)) return;
    call(
      () => axios.delete(`${API}/vehicles/engines`, { params: { make, model, engine }, withCredentials: true }),
      "Engine removed"
    );
  };

  return (
    <div data-testid="vehicle-manager" className="glass-panel rounded-xl p-6 mb-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-nitro mb-1">
            Customer Dropdowns
          </p>
          <h2 className="font-heading font-bold uppercase text-2xl text-neutral-100">
            Vehicle Catalogue
          </h2>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="make-add-input"
            value={newMake}
            onChange={(e) => setNewMake(e.target.value)}
            placeholder="e.g. Vauxhall"
            className={`${numInputCls} w-40`}
          />
          <Button
            data-testid="make-add-button"
            onClick={addMake}
            disabled={busy || !newMake.trim()}
            className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-wider rounded-full px-5 disabled:opacity-60"
          >
            <Plus size={15} className="mr-1" /> Make
          </Button>
        </div>
      </div>

      {catalogue === null && <Loader2 className="animate-spin text-nitro" size={20} />}

      {catalogue?.map((mk) => (
        <div key={mk.make} data-testid="catalogue-make" className="border border-[#262933] rounded-lg mb-2 bg-[#0E1015]/60">
          <div
            className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-panel/60 transition-colors"
            onClick={() => {
              setOpenMake(openMake === mk.make ? null : mk.make);
              setOpenModel(null);
            }}
          >
            <span className="flex items-center gap-2 font-heading font-semibold uppercase tracking-wide text-neutral-100">
              <ChevronRight
                size={16}
                className={`text-nitro transition-transform duration-200 ${openMake === mk.make ? "rotate-90" : ""}`}
              />
              {mk.make}
              <span className="font-mono text-[10px] text-neutral-500 normal-case tracking-normal">
                {mk.models.length} model{mk.models.length === 1 ? "" : "s"}
              </span>
            </span>
            <button
              data-testid={`make-delete-${mk.make}`}
              onClick={(e) => {
                e.stopPropagation();
                deleteMake(mk.make);
              }}
              aria-label={`Delete ${mk.make}`}
              className="text-neutral-500 hover:text-nitro transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          {openMake === mk.make && (
            <div className="px-4 pb-4 pt-3 border-t border-[#1F232D]">
              {mk.models.map((mo) => (
                <div key={mo.name} className="mb-2">
                  <div className="flex items-center justify-between px-3 py-2 rounded-md border border-[#1F232D] bg-panel/50">
                    <button
                      className="flex items-center gap-2 text-sm text-neutral-200 hover:text-nitro transition-colors"
                      onClick={() => setOpenModel(openModel === mo.name ? null : mo.name)}
                    >
                      <Car size={14} className="text-neutral-500" />
                      {mo.name}
                      <span className="font-mono text-[10px] text-neutral-500">
                        {mo.engines.length} engine{mo.engines.length === 1 ? "" : "s"}
                      </span>
                    </button>
                    <button
                      data-testid={`model-delete-${mo.name}`}
                      onClick={() => deleteModel(mk.make, mo.name)}
                      aria-label={`Delete ${mo.name}`}
                      className="text-neutral-500 hover:text-nitro transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {openModel === mo.name && (
                    <div className="mt-2 ml-4 pl-4 border-l border-[#262933]">
                      {mo.engines.map((e) => (
                        <div
                          key={e.name}
                          className="flex items-center justify-between py-1.5 text-xs font-mono text-neutral-400"
                        >
                          <span>
                            {e.name}
                            <span className="text-neutral-600"> · {e.fuel} · </span>
                            <span className="text-neutral-300">
                              {e.stock_bhp} → <span className="text-nitro">{e.tuned_bhp}</span> BHP
                            </span>
                          </span>
                          <button
                            data-testid={`engine-delete-${e.name}`}
                            onClick={() => deleteEngine(mk.make, mo.name, e.name)}
                            aria-label={`Delete ${e.name}`}
                            className="text-neutral-500 hover:text-nitro transition-colors"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}

                      <div className="mt-3 flex flex-col gap-2">
                        <Input
                          data-testid="engine-name-input"
                          value={eng.name}
                          onChange={(e2) => setEng((s) => ({ ...s, name: e2.target.value }))}
                          placeholder="Engine name, e.g. 1.0L EcoBoost 125"
                          className={numInputCls}
                        />
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          <Select
                            value={eng.fuel}
                            onValueChange={(v) => setEng((s) => ({ ...s, fuel: v }))}
                          >
                            <SelectTrigger data-testid="engine-fuel-select" className={numInputCls}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-panel border-[#262933] text-neutral-100">
                              <SelectItem value="petrol" className="focus:bg-nitro/20 focus:text-white">Petrol</SelectItem>
                              <SelectItem value="diesel" className="focus:bg-nitro/20 focus:text-white">Diesel</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input data-testid="engine-stock-bhp" type="number" min="0" placeholder="Stock BHP" value={eng.stock_bhp} onChange={(e2) => setEng((s) => ({ ...s, stock_bhp: e2.target.value }))} className={numInputCls} />
                          <Input data-testid="engine-stock-nm" type="number" min="0" placeholder="Stock Nm" value={eng.stock_nm} onChange={(e2) => setEng((s) => ({ ...s, stock_nm: e2.target.value }))} className={numInputCls} />
                          <Input data-testid="engine-tuned-bhp" type="number" min="0" placeholder="Tuned BHP" value={eng.tuned_bhp} onChange={(e2) => setEng((s) => ({ ...s, tuned_bhp: e2.target.value }))} className={numInputCls} />
                          <Input data-testid="engine-tuned-nm" type="number" min="0" placeholder="Tuned Nm" value={eng.tuned_nm} onChange={(e2) => setEng((s) => ({ ...s, tuned_nm: e2.target.value }))} className={numInputCls} />
                        </div>
                        <Button
                          data-testid="engine-add-button"
                          onClick={() => addEngine(mk.make, mo.name)}
                          disabled={busy || !engineReady}
                          className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-wider rounded-full px-5 disabled:opacity-60 w-fit"
                        >
                          <Plus size={15} className="mr-1" /> Add Engine
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex gap-2 mt-3">
                <Input
                  data-testid="model-add-input"
                  value={newModel}
                  onChange={(e) => setNewModel(e.target.value)}
                  placeholder="Add model, e.g. Corsa (F)"
                  className={`${numInputCls} w-56`}
                />
                <Button
                  data-testid="model-add-button"
                  onClick={() => addModel(mk.make)}
                  disabled={busy || !newModel.trim()}
                  variant="outline"
                  className="border-[#262933] bg-transparent hover:bg-panel hover:border-nitro text-neutral-100 font-heading uppercase tracking-wider rounded-full px-5 disabled:opacity-50"
                >
                  <Plus size={14} className="mr-1" /> Model
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      <p className="mt-4 font-mono text-[9px] tracking-[0.2em] uppercase text-neutral-600">
        Changes appear in the customer dropdowns instantly
      </p>
    </div>
  );
};
